<template>
  <li
    v-if="variantExists"
    :class="!isVariantAvailable ? 'oos' : 'is'"
    :data-testid="!isVariantAvailable ? 'configurable-sizes-option-oos' : 'configurable-sizes-option-is'"
  >
    <input
      :id="id"
      :checked="selectedOption && selectedOption.value_index === option.value_index"
      class="configurable__input"
      type="radio"
      :name="product.id + '_' + attribute.attribute_code"
      :value="option.value_index"
      @change="selected()"
    >
    <label
      :for="id"
      class="configurable__label"
    >
      <span
        class="configurable__value"
        v-text="option.local_size ? option.local_size : option.label"
      />
      <span
        v-if="!isVariantAvailable"
        class="configurable__oos"
      >({{ $t('Notify me') }})</span>
    </label>
  </li>
</template>

<script lang="ts" setup>
import type { MagentoConfigurableProduct, MagentoConfigurableProductOption, MagentoConfigurableProductOptionsValue } from '~layers/app/types/magento'

export interface Props {
  product: MagentoConfigurableProduct
  attribute: MagentoConfigurableProductOption
  option: MagentoConfigurableProductOptionsValue
  selectedOptions?: Record<string, MagentoConfigurableProductOptionsValue>
}

const props = defineProps<Props>()
const selectedOptions = toRef(props.selectedOptions || [])

const emit = defineEmits<{
  selected: MagentoConfigurableProductOptionsValue
}>()

const selected = () => {
  emit('selected', props.option)
}

const selectedOption = computed<MagentoConfigurableProductOptionsValue | null>(() =>
  selectedOptions.value[props.attribute.attribute_code] || null,
)

const id = computed(() =>
  `product_${props.product.id}_${props.attribute.attribute_code}_${props.option.value_index}`,
)

const variantExists = computed<boolean>(() => {
  return !!props.product.variants.find((variant) => {
    return variant.attributes[0].value_index === props.option.value_index
  })
})

const currentOptions = computed<Record<string, MagentoConfigurableProductOptionsValue>>(() => {
  const optionsToCheck = { ...selectedOptions.value }
  optionsToCheck[props.attribute.attribute_code] = props.option
  return optionsToCheck
})

const currentVariant = computed<never | null>(() => {
  if (Object.values(currentOptions.value).length < props.product.configurable_options.length) {
    return null
  }

  return getVariantByAttributeOptions(currentOptions.value)
})

const getVariantByAttributeOptions = (options: Record<string, MagentoConfigurableProductOptionsValue>): never | null => {
  return props.product.variants.find((variant) => {
    const variantAttributeOptions = variant.attributes.reduce((acc: Record<string, number>, option: MagentoConfigurableProductOptionsValue) => {
      return { ...acc, [option.code]: option.value_index }
    }, {})

    for (const i in options) {
      if (variantAttributeOptions[i] === undefined || variantAttributeOptions[i] !== options[i].value_index) {
        return false
      }
    }
    return true
  }) || null
}

const isVariantAvailable = computed<boolean>(() => {
  if (props.product.stock_status !== 'IN_STOCK') {
    return false
  }

  if (!currentVariant.value) {
    return true
  }

  return currentVariant.value.product.stock_status === 'IN_STOCK'
})
</script>
